import React, { useEffect, useMemo, useState } from "react";
import { Languages } from "lucide-react";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";

/**
 * ContentLanguageSelect
 * ─────────────────────
 * Compact per-widget picker for the LANGUAGE the AI should WRITE the
 * content in. Distinct from the header LanguageSwitcher (which changes
 * the dashboard UI language). Emits an ISO code via `onChange`.
 *
 * • Auto-defaults to the user's stored content_language preference the
 *   first time it mounts, so writers stay in the language they picked.
 * • Locally overrideable — the user can jump into Patois for one bulletin
 *   without changing their global preference.
 * • Falls back to English silently when the catalogue is unreachable.
 */
export default function ContentLanguageSelect({
  value,
  onChange,
  label = "AI writes in",
  testid = "content-language-select",
  className = "",
  compact = false,
}) {
  const [catalog, setCatalog] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: cat }, prefRes] = await Promise.all([
          api.get("/i18n/languages"),
          value ? Promise.resolve(null) : api.get("/i18n/preference").catch(() => null),
        ]);
        if (cancelled) return;
        setCatalog(cat);
        if (!value && prefRes?.data?.content_language) {
          onChange?.(prefRes.data.content_language);
        }
      } catch {
        if (!cancelled) setCatalog({ launch: [], phase1: [], caribbean: [], all: [] });
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const current = useMemo(
    () => catalog?.all?.find((l) => l.code === (value || "en")),
    [catalog, value],
  );

  const sections = [
    { key: "launch",    label: "LAUNCH",       rows: catalog?.launch    || [] },
    { key: "phase1",    label: "PHASE 1",      rows: catalog?.phase1    || [] },
    { key: "caribbean", label: "CARIBBEAN · USP", rows: catalog?.caribbean || [] },
  ];

  return (
    <div className={className} data-testid={`${testid}-wrap`}>
      {label && !compact && (
        <Label className="type-card-meta-label uppercase tracking-wider text-ink-secondary flex items-center gap-1">
          <Languages className="h-3 w-3" /> {label}
        </Label>
      )}
      <Select value={value || "en"} onValueChange={onChange}>
        <SelectTrigger
          className={`${compact ? "h-8 w-32 text-xs" : "mt-1 w-44"} bg-bg border-line`}
          data-testid={testid}
        >
          <SelectValue>
            <span className="flex items-center gap-1.5">
              <span className="text-sm leading-none">{current?.flag || "🌐"}</span>
              <span className="font-mono uppercase tracking-wide">{current?.code || "en"}</span>
              {!compact && <span className="text-ink-muted truncate">{current?.native}</span>}
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="max-h-[60vh]">
          {sections.map((s) => s.rows.length > 0 && (
            <SelectGroup key={s.key}>
              <SelectLabel className="text-[10px] font-mono uppercase tracking-[0.18em] text-cyan-300">
                // {s.label}
              </SelectLabel>
              {s.rows.map((l) => (
                <SelectItem key={l.code} value={l.code}
                  data-testid={`${testid}-opt-${l.code}`}>
                  <span className="flex items-center gap-2">
                    <span>{l.flag}</span>
                    <span className="font-medium">{l.native}</span>
                    <span className="text-ink-muted text-xs">({l.name})</span>
                  </span>
                </SelectItem>
              ))}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
